import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Iconography,
  Modal,
  useModal,
  Tooltip,
  StatusBanner,
} from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import { DOCUMENT_CATEGORY } from '../../../../../constants/constants';
import { groupFilesByCategory } from '../../../../../api/services/lupp/order/transformers.order';
import type { FilesByCategory } from '../../../../../api/services/lupp/order/transformers.order';
import {
  useGetServiceCenters,
  useCreateInvoice,
  useUpdateOrder,
} from '../../../../../api/services/lupp/order/order';
import { formatDateForLanguage } from '../../../../../utils/tools';
import { useContentfulLocale } from '../../../../../hooks/useContentfulLocale';
import DeliveryAcceptance from './DeliveryAcceptance/DeliveryAcceptance';
import InvoicesTable from './InvoicesTable/InvoicesTable';
import ServiceCenter from './ServiceCenter/ServiceCenter';
import { isEmbeddedSubventionMethod, NOT_APPLICABLE_FALLBACK } from '../PaymentFinancing/constants';
import { useUserIdentity } from '@lucid/portal-core';
import styles from './CustomerHandoff.module.scss';

/**
 * Mapping from API error codes to Contentful label keys and English fallbacks.
 * label keys must match the Contentful entry fields for the manage-orders labels entry.
 */
const INVOICE_ERROR_CODE_CONFIG: Record<string, { labelKey: string; fallback: string }> = {
  VIN_NOT_ASSIGNED: {
    labelKey: 'invoiceError.VIN_NOT_ASSIGNED',
    fallback: 'VIN has not been assigned to the delivery',
  },
  SCHEDULED_DATE_MISSING: {
    labelKey: 'invoiceError.SCHEDULED_DATE_MISSING',
    fallback: 'Scheduled Final Delivery Date is not set',
  },
  SCHEDULED_DATE_OUT_OF_WINDOW: {
    labelKey: 'invoiceError.SCHEDULED_DATE_OUT_OF_WINDOW',
    fallback: 'Scheduled date not within 14-day window',
  },
  OFAC_NOT_COMPLETED: {
    labelKey: 'invoiceError.OFAC_NOT_COMPLETED',
    fallback: 'OFAC screening has not been completed for the buyer',
  },
  OFAC_POTENTIAL_MATCH: {
    labelKey: 'invoiceError.OFAC_POTENTIAL_MATCH',
    fallback: 'OFAC screening returned a potential match',
  },
  ORDER_NOT_FOUND: {
    labelKey: 'invoiceError.ORDER_NOT_FOUND',
    fallback: 'Order not found',
  },
  DELIVERY_NOT_FOUND: {
    labelKey: 'invoiceError.DELIVERY_NOT_FOUND',
    fallback: 'No Delivery record found for Order',
  },
  DUPLICATE_FILE_REQUEST: {
    labelKey: 'invoiceError.DUPLICATE_FILE_REQUEST',
    fallback: 'A file request with the same ID already exists',
  },
};

type CustomerHandoffProps = {
  orderData: OrderDetailsResponse;
  filesByCategory?: FilesByCategory;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Customer Handoff section displaying final invoices for an order.
 * Allows agents to generate, view, and download invoices.
 *
 * @component
 * @param {CustomerHandoffProps} props - Component props
 * @returns {JSX.Element} Customer Handoff section
 *
 * @example
 * ```tsx
 * <CustomerHandoff orderData={orderData} theme="dark" labels={labels} />
 * ```
 */
const CustomerHandoff: React.FC<CustomerHandoffProps> = ({
  orderData,
  filesByCategory,
  theme,
  labels,
  dataTestId = 'customer-handoff',
}) => {
  const contentfulLocale = useContentfulLocale();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [signingCompleted, setSigningCompleted] = useState(false);
  const [selectedServiceCenter, setSelectedServiceCenter] = useState('');
  const [serviceCenterSuccess, setServiceCenterSuccess] = useState('');
  const [serviceCenterError, setServiceCenterError] = useState('');
  const successModal = useModal();
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  const { userIdentity } = useUserIdentity();
  const { mutate: createInvoice, isPending: isGeneratingInvoice } = useCreateInvoice();
  const { mutate: updateOrder, isPending: isCompletingHandoff } = useUpdateOrder();

  // Use pre-grouped map from parent when available; compute as fallback
  const resolvedFilesByCategory = useMemo(
    () => filesByCategory ?? groupFilesByCategory(orderData.moduleFileReferenceDtoList),
    [filesByCategory, orderData.moduleFileReferenceDtoList]
  );

  // TODO: Wire up once quality check integration is complete
  // const isFinalQualityCheckCompleted = useMemo(
  //   () =>
  //     orderData?.moduleFileReferenceDtoList?.some(
  //       (file) => file.documentCategory === DOCUMENT_CATEGORY.FINAL_QUALITY_CHECK
  //     ) ?? false,
  //   [orderData?.moduleFileReferenceDtoList]
  // );

  const displayedInvoices = useMemo(
    () => resolvedFilesByCategory[DOCUMENT_CATEGORY.FINAL_INVOICE] ?? [],
    [resolvedFilesByCategory]
  );

  const isEmbedded = isEmbeddedSubventionMethod(orderData.subventionMethod);

  const isInvoiceGenerated = useMemo(
    () => displayedInvoices.some((inv) => inv.fileGenerationStatus === 'GENERATED'),
    [displayedInvoices]
  );

  const hasDeliveryDocs = useMemo(
    () =>
      (orderData.moduleFileReferenceDtoList ?? []).some(
        (doc) =>
          doc.documentCategory === DOCUMENT_CATEGORY.DELIVERY_ACCEPTANCE ||
          doc.documentCategory === DOCUMENT_CATEGORY.DUE_BILL
      ),
    [orderData.moduleFileReferenceDtoList]
  );

  const { data: serviceCenters } = useGetServiceCenters({
    country: userIdentity?.countryCode,
    enabled: true,
  });

  const serviceCenterLocationOptions = useMemo(
    () =>
      (serviceCenters ?? []).map((sc) => ({
        value: sc.sfdcServiceTerritoryId,
        label: `${sc.publicName} - ${sc.city}, ${sc.country}`,
        sfdcAccountId: sc.sfdcAccountId ?? null,
        partnerPortalAccountRefId: sc.partnerPortalAccountRefId ?? null,
      })),
    [serviceCenters]
  );

  // Initialize selected service center from saved value once options are loaded
  useEffect(() => {
    if (serviceCenterLocationOptions.length > 0 && orderData.preferredServiceCenterLocation) {
      const savedOption = serviceCenterLocationOptions.find(
        (opt) => opt.label === orderData.preferredServiceCenterLocation
      );
      if (savedOption) {
        setSelectedServiceCenter(savedOption.value);
      }
    }
  }, [serviceCenterLocationOptions, orderData.preferredServiceCenterLocation]);

  const handleGenerateInvoice = useCallback(() => {
    setShowError(false);
    setErrorMessage('');
    setErrorDetails([]);

    createInvoice(
      {
        orderIdentifier: orderData.orderIdentifier,
        invoiceType: 'FINAL_INVOICE',
      },
      {
        onSuccess: () => {
          successModal.openModal();
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          // The API layer attaches the raw response body as `responseData` on the Error object.
          // Use errorCodes (structured) when available; fall back to splitting the message string.
          const responseData = error?.responseData as { errorCodes?: string[] } | undefined;
          const errorCodes = responseData?.errorCodes ?? [];

          let errorMessages: string[];

          if (errorCodes.length > 0) {
            errorMessages = errorCodes.map((code) => {
              const config = INVOICE_ERROR_CODE_CONFIG[code];
              return config ? (labels?.[config.labelKey] ?? config.fallback) : code;
            });
          } else {
            // Fallback when errorCodes are absent: split the compound message on ";"
            const rawMessage =
              error?.message ||
              labels?.invoiceErrorGeneric ||
              'Invoice not available, Please try again';
            errorMessages = rawMessage
              .split(/;\s*/)
              .map((s: string) => s.trim())
              .filter(Boolean);
          }

          if (errorMessages.length > 1) {
            setErrorMessage(labels?.invoiceErrorTitle || 'Unable to generate Final Invoice:');
            setErrorDetails(errorMessages);
          } else {
            setErrorMessage(
              errorMessages[0] ??
                labels?.invoiceErrorGeneric ??
                'Invoice not available, Please try again'
            );
            setErrorDetails([]);
          }
          setShowError(true);
        },
      }
    );
  }, [createInvoice, orderData.orderIdentifier, successModal, labels]);

  const handleCloseSuccessModal = useCallback(() => {
    successModal.closeModal();
  }, [successModal]);

  const handleSigningComplete = useCallback((serviceCenterLocation: string | null) => {
    setSigningCompleted(true);
    if (serviceCenterLocation) {
      setSelectedServiceCenter(serviceCenterLocation);
      setServiceCenterSuccess('');
      setServiceCenterError('');
    }
  }, []);

  const handleComplete = useCallback(
    (formData: Record<string, unknown>) => {
      const selectedValue = formData.preferredServiceCenterLocation as string;
      if (!selectedValue) return;
      const selectedOption = serviceCenterLocationOptions.find(
        (opt) => opt.value === selectedValue
      );
      setServiceCenterSuccess('');
      setServiceCenterError('');
      updateOrder(
        {
          orderIdentifier: orderData.orderIdentifier,
          preferredServiceCenterLocation: selectedOption?.label,
          preferredSfdcServiceTerritoryId: selectedValue,
          preferredArSfdcAccountId: selectedOption?.sfdcAccountId ?? null,
          preferredArPartnerPortalAccountRefId: selectedOption?.partnerPortalAccountRefId ?? null,
        },
        {
          onSuccess: () => {
            setServiceCenterSuccess(
              labels?.serviceCenterSaveSuccess || 'Preferred service center saved successfully.'
            );
            setTimeout(() => setServiceCenterSuccess(''), 3000);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: (error: any) => {
            const errorData = error?.response?.data;
            setServiceCenterError(
              errorData?.message ||
                labels?.serviceCenterSaveError ||
                'Failed to save preferred service center. Please try again.'
            );
          },
        }
      );
    },
    [serviceCenterLocationOptions, updateOrder, orderData.orderIdentifier, labels]
  );

  return (
    <Box data-testid={dataTestId} className={styles.finalInvoiceSection}>
      <Box mb={16} className={styles.finalInvoiceHeader}>
        <Typography variant="subtitle" size="small" theme={theme} color="black">
          {labels?.finalInvoiceTitle || 'Final Invoice'}
        </Typography>
        <Tooltip
          content={
            <Box>
              <Typography variant="body" size="small" theme={theme === 'dark' ? 'light' : 'dark'}>
                {labels?.finalInvoiceTooltipTitle || 'Final Invoice can only be generated when:'}
              </Typography>
              <ul className={styles.tooltipList}>
                <li>{labels?.finalInvoiceTooltipVin || 'VIN is assigned to the order'}</li>
                <li>
                  {labels?.finalInvoiceTooltipDeliveryDate ||
                    'Scheduled delivery date is populated'}
                </li>
                <li>
                  {labels?.finalInvoiceTooltipDeliveryWindow ||
                    'Scheduled delivery date is within 14 days of today'}
                </li>
                <li>
                  {labels?.finalInvoiceTooltipRemainingAmount ||
                    'Remaining amount at handoff must be zero'}
                </li>
                <li>{labels?.finalInvoiceTooltipOfacComplete || 'OFAC check is complete'}</li>
                <li>
                  {labels?.finalInvoiceTooltipOfacNoMatch ||
                    'OFAC check does not result in a potential match'}
                </li>
              </ul>
            </Box>
          }
          side="bottom"
          theme={theme === 'dark' ? 'light' : 'dark'}
          dataTestId={`${dataTestId}-final-invoice-tooltip`}
        >
          <span className={styles.tooltipIcon} role="img" aria-label="More information">
            <Iconography category="global" name="information" color="gray-5" theme={theme} />
          </span>
        </Tooltip>
      </Box>

      {isEmbedded && (
        <Box mb={24}>
          <StatusBanner
            type="info"
            message={labels?.['financeProvider.notApplicable'] ?? NOT_APPLICABLE_FALLBACK}
            dataTestId="final-invoice-not-applicable"
          />
        </Box>
      )}

      {!isEmbedded && displayedInvoices.length ? (
        <Box mb={24}>
          <InvoicesTable
            invoicesData={displayedInvoices}
            sfdcDeliveryId={orderData.sfdcDeliveryIdentifier || ''}
            theme={theme}
            labels={labels}
          />
        </Box>
      ) : null}

      {showError && errorMessage && (
        <Box mb={24} className={styles.errorBox} data-testid="final-invoice-error">
          <Box className={styles.errorHeader}>
            <Iconography
              category="global"
              name="information"
              color="error-1"
              theme={theme}
              dataTestId="error-icon"
            />
            <Typography variant="body" size="medium" theme={theme} color="error-1">
              {errorMessage}
            </Typography>
          </Box>
          {errorDetails.length > 0 && (
            <ol className={styles.errorList} data-testid="error-details">
              {errorDetails.map((detail) => (
                <li key={detail}>
                  <Typography variant="body" size="small" theme={theme} color="black">
                    {detail}
                  </Typography>
                </li>
              ))}
            </ol>
          )}
        </Box>
      )}

      {!isEmbedded && !displayedInvoices.length && (
        <Button
          label={
            isGeneratingInvoice
              ? labels?.generatingInvoiceButton || 'Generating...'
              : labels?.generateInvoiceButton || 'Generate Invoice'
          }
          size="medium"
          type="secondary"
          color="neutral"
          theme={invertedTheme}
          onClick={handleGenerateInvoice}
          gaComponent="customer-handoff-generate-invoice"
          dataTestId="generate-invoice-button"
          disabled={isGeneratingInvoice}
        />
      )}

      <DeliveryAcceptance
        orderData={orderData}
        isFinalQualityCheckCompleted={true}
        isInvoiceGenerated={isInvoiceGenerated}
        isEmbeddedFinance={isEmbedded}
        serviceCenterLocationOptions={serviceCenterLocationOptions}
        theme={theme}
        labels={labels}
        onSigningComplete={handleSigningComplete}
        dataTestId="delivery-acceptance"
      />

      {(signingCompleted || hasDeliveryDocs) && (
        <ServiceCenter
          labels={labels}
          theme={theme}
          tableTheme={invertedTheme}
          options={serviceCenterLocationOptions}
          selectedLocation={selectedServiceCenter}
          onComplete={handleComplete}
          isCompleting={isCompletingHandoff}
          successMessage={serviceCenterSuccess}
          errorMessage={serviceCenterError}
          handoffDateDisplay={formatDateForLanguage(orderData.handOffDate, contentfulLocale)}
          warrantyStartDateDisplay={formatDateForLanguage(
            orderData.warrantyStartDate,
            contentfulLocale
          )}
          dataTestId="preferred-service-center"
        />
      )}

      <Modal
        className={styles.modal}
        isOpen={successModal.isOpen}
        onClose={handleCloseSuccessModal}
        title={labels?.invoiceSuccessTitle || 'Invoice Successfully Generated'}
        theme={invertedTheme}
        size="medium"
        dataTestId="invoice-success-modal"
        footer={
          <Box className={styles.modalFooter}>
            <Button
              label={labels?.closeButton || 'CLOSE'}
              type="primary"
              color="neutral"
              theme={invertedTheme}
              handleClick={handleCloseSuccessModal}
              gaComponent="invoice-success-close"
              dataTestId="invoice-success-close-button"
              isFluid
            />
          </Box>
        }
      >
        <Box className={styles.modalContent}>
          <Typography
            variant="body"
            size="medium"
            theme={invertedTheme}
            color="white"
            align="center"
          >
            {labels?.invoiceSuccessMessage ||
              'The Final invoice has been successfully saved to the order.'}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

CustomerHandoff.displayName = 'CustomerHandoff';

export default CustomerHandoff;
